import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Slider,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Chip,
  Rating,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Star as StarIcon
} from '@mui/icons-material';
// Firebase imports removed - using SQL API now
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
// import { database } from '../../../firebase';
import { storage } from '../../../firebase';
import { getReferenceSettings, saveReferenceSettings, addReferenceItem, updateReferenceItem, deleteReferenceItem } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import type { CorporateHeroSectionSettings } from '../../../types/corporateHero';
import { defaultCorporateHeroSectionSettings } from '../../../types/corporateHero';
import { buildCorporateHeroSavePayload, normalizeCorporateHeroSection } from '../../../utils/corporateHeroSettings';
import CorporateHeroSettingsPanel from './corporateHero/CorporateHeroSettingsPanel';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';

interface Reference {
  id: string;
  companyName: string;
  logoUrl?: string;
  websiteUrl?: string;
  description?: string;
  category?: string;
  active: boolean;
}

interface ReferencesSettings {
  heroSection: CorporateHeroSectionSettings;
  references: Reference[];
  displaySettings: {
    itemsPerRow: number;
    showCompanyName: boolean;
    showDescription: boolean;
    showCategory: boolean;
    logoSize: 'small' | 'medium' | 'large';
  };
}

const defaultReferencesSettings: ReferencesSettings = {
  heroSection: {
    ...defaultCorporateHeroSectionSettings,
    title: 'Our References',
    description: 'Our trusted business partners and institutions we work with.',
    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    height: 400,
    templateType: 'three-column-stats',
  },
  references: [],
  displaySettings: {
    itemsPerRow: 4,
    showCompanyName: false,
    showDescription: false,
    showCategory: true,
    logoSize: 'medium'
  }
};

interface ReferencesSettingsComponentProps {
  companyId: string | null;
  configId: string; // ✅ SQL için configId eklendi
  settings?: ReferencesSettings | null;
  onSettingsChange?: (settings: ReferencesSettings) => void;
}

const ReferencesSettingsComponent: React.FC<ReferencesSettingsComponentProps> = ({ 
  companyId,
  configId, // ✅ SQL için configId
  settings: externalSettings, 
  onSettingsChange 
}) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<ReferencesSettings>(defaultReferencesSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReference, setEditingReference] = useState<Reference | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [referenceConfigId, setReferenceConfigId] = useState<string | null>(null); // SQL ReferenceConfig ID
  
  // Auto-save için debounce timer
  const autoSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Ayarları yükle (SQL'den)
  useEffect(() => {
    const loadSettings = async () => {
      if (!companyId || !configId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🔄 [REFERENCES] SQL den yükleniyor, ConfigId:', configId);
        
        const response = await getReferenceSettings(companyId, configId); // ✅ companyId eklendi
        
        if (response && response.success && response.data) {
          const data = response.data;
          
          // ReferenceConfig ID'yi sakla
          setReferenceConfigId(data.id);
          
          // Settings'i oluştur
          const loadedSettings: ReferencesSettings = {
            heroSection: normalizeCorporateHeroSection(data, {
              title: data.heroTitle || defaultReferencesSettings.heroSection.title,
              description: data.heroDescription || defaultReferencesSettings.heroSection.description,
              backgroundColor: data.heroBackgroundColor || defaultReferencesSettings.heroSection.backgroundColor,
              height: data.heroHeight || defaultReferencesSettings.heroSection.height,
            }),
            references: data.references || [],
            displaySettings: {
              itemsPerRow: data.itemsPerRow || 4,
              showCompanyName: data.showCompanyName || false,
              showDescription: data.showDescription || false,
              showCategory: data.showCategory !== undefined ? data.showCategory : true,
              logoSize: (data.logoSize as 'small' | 'medium' | 'large') || 'medium'
            }
          };
          
          setSettings(loadedSettings);
          console.log('✅ [REFERENCES] SQL den yükleme başarılı, Referans sayısı:', data.references?.length || 0);
        } else {
          // Veri yoksa default kullan
          console.log('ℹ️ [REFERENCES] Veri bulunamadı, default ayarlar kullanılıyor');
          setSettings(defaultReferencesSettings);
        }
        
      } catch (err) {
        console.error('❌ [REFERENCES] Ayarlar yüklenirken hata:', err);
        setError('An error occurred while loading settings');
        setSettings(defaultReferencesSettings);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [companyId, configId]); // ✅ externalSettings ve onSettingsChange kaldırıldı (sonsuz döngü önlemi)

  // Otomatik kayıt (Debounced)
  const autoSave = async (settingsToSave: ReferencesSettings) => {
    if (!companyId || !configId) {
      console.warn('⚠️ [REFERENCES AUTO-SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor');
      return;
    }

    try {
      console.log('💾 [REFERENCES AUTO-SAVE] Otomatik kaydediliyor...');
      
      // Ana ayarları kaydet
      const response = await saveReferenceSettings({
        companyId,
        configId,
        ...buildCorporateHeroSavePayload(settingsToSave.heroSection),
        itemsPerRow: settingsToSave.displaySettings.itemsPerRow,
        showCompanyName: settingsToSave.displaySettings.showCompanyName,
        showDescription: settingsToSave.displaySettings.showDescription,
        showCategory: settingsToSave.displaySettings.showCategory,
        logoSize: settingsToSave.displaySettings.logoSize
      });

      if (response && response.success) {
        // ReferenceConfig ID'yi güncelle
        if (response.data && response.data.id) {
          setReferenceConfigId(response.data.id);
        }
        
        console.log('✅ [REFERENCES AUTO-SAVE] Otomatik kayıt başarılı');
        notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
      }
    } catch (err) {
      console.error('❌ [REFERENCES AUTO-SAVE] Otomatik kayıt hatası:', err);
    }
  };

  // Debounced auto-save
  const debouncedAutoSave = (settingsToSave: ReferencesSettings) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSave(settingsToSave);
    }, 1000); // 1 saniye bekle
  };

  const updateHeroSection = (heroSection: CorporateHeroSectionSettings) => {
    setSettings((prev) => {
      const newSettings = {
        ...prev,
        heroSection,
      };
      debouncedAutoSave(newSettings);
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }
      return newSettings;
    });
  };

  // Display settings güncelle (Auto-save ile)
  const updateDisplaySettings = (field: keyof typeof settings.displaySettings, value: any) => {
    const newSettings = {
      ...settings,
      displaySettings: {
        ...settings.displaySettings,
        [field]: value
      }
    };
    setSettings(newSettings);
    
    // Auto-save tetikle
    debouncedAutoSave(newSettings);
    
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Yeni referans ekle
  const handleAddReference = () => {
    const newReference: Reference = {
      id: Date.now().toString(),
      companyName: '',
      category: '',
      active: true
    };
    setEditingReference(newReference);
    setEditDialogOpen(true);
  };

  // Referans düzenle
  const handleEditReference = (reference: Reference) => {
    setEditingReference({ ...reference });
    setImagePreview(reference.logoUrl || null);
    setEditDialogOpen(true);
  };

  // Referans sil (SQL'den)
  const handleDeleteReference = async (referenceId: string) => {
    if (!companyId) { // ✅ companyId null check
      console.warn('⚠️ [REFERENCES] CompanyId yok, silme atlanıyor');
      return;
    }

    try {
      console.log('🗑️ [REFERENCES] Referans siliniyor:', referenceId);
      
      const response = await deleteReferenceItem(referenceId, companyId); // ✅ companyId eklendi
      
      if (response && response.success) {
        // Local state'i güncelle
        const newSettings = {
          ...settings,
          references: settings.references.filter(ref => ref.id !== referenceId)
        };
        setSettings(newSettings);
        
        setSuccess('Referans silindi');
        setTimeout(() => setSuccess(null), 3000);
        
        console.log('✅ [REFERENCES] Referans başarıyla silindi');
      }
    } catch (error) {
      console.error('❌ [REFERENCES] Referans silme hatası:', error);
      setError('An error occurred while deleting reference');
    }
  };

  // Logo yükleme (Firebase Storage)
  const handleImageUpload = async () => {
    if (!imageFile || !companyId) return null;

    try {
      setImageUploading(true);
      console.log('📤 [REFERENCES] Logo Firebase Storage a yükleniyor...');
      
      const imagePath = `companies/${companyId}/references/logos/${Date.now()}_${imageFile.name}`;
      const imageStorageRef = storageRef(storage, imagePath);
      await uploadBytes(imageStorageRef, imageFile);
      const downloadURL = await getDownloadURL(imageStorageRef);
      
      console.log('✅ [REFERENCES] Logo başarıyla yüklendi:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('❌ [REFERENCES] Logo yükleme hatası:', error);
      setError('An error occurred while uploading logo');
      return null;
    } finally {
      setImageUploading(false);
    }
  };

  // Referans kaydet (SQL'e)
  const handleSaveReference = async () => {
    if (!editingReference || !referenceConfigId) {
      setError('Reference info or Config ID not found');
      return;
    }

    try {
      let logoUrl: string | undefined = editingReference.logoUrl;
      
      // Logo yükleme
      if (imageFile) {
        const uploadedUrl = await handleImageUpload();
        if (!uploadedUrl) return;
        logoUrl = uploadedUrl;
      }

      const referenceData = {
        referenceConfigId: referenceConfigId,
        companyId: companyId || '',
        companyName: editingReference.companyName,
        logoUrl: logoUrl,
        websiteUrl: editingReference.websiteUrl,
        description: editingReference.description,
        category: editingReference.category,
        displayOrder: settings.references.length
      };

      // Yeni mi yoksa güncelleme mi?
      const isExisting = editingReference.id && settings.references.find(ref => ref.id === editingReference.id);
      
      let response;
      if (isExisting) {
        // Güncelleme
        console.log('🔄 [REFERENCES] Referans güncelleniyor:', editingReference.id);
        response = await updateReferenceItem(editingReference.id, referenceData);
      } else {
        // Yeni ekleme
        console.log('➕ [REFERENCES] Yeni referans ekleniyor');
        response = await addReferenceItem(referenceData);
      }

      if (response && response.success) {
        // Local state'i güncelle
        const updatedReference = { 
          ...editingReference, 
          logoUrl,
          id: response.data?.id || editingReference.id 
        };
        
        const newSettings = {
          ...settings,
          references: isExisting
            ? settings.references.map(ref => ref.id === editingReference.id ? updatedReference : ref)
            : [...settings.references, updatedReference]
        };

        setSettings(newSettings);
        
        setEditDialogOpen(false);
        setEditingReference(null);
        setImageFile(null);
        setImagePreview(null);
      setSuccess(isExisting ? 'Reference updated' : 'Reference added');
        setTimeout(() => setSuccess(null), 3000);
        
        console.log('✅ [REFERENCES] Referans başarıyla kaydedildi');
      }
    } catch (error) {
      console.error('❌ [REFERENCES] Referans kaydetme hatası:', error);
      setError('An error occurred while saving reference');
    }
  };

  // Resim dosyası seçimi
  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size cannot be larger than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          <BusinessIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          {t('ecommerceReferencesSettings.title')}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Hero Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceReferencesSettings.sections.hero')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CorporateHeroSettingsPanel
            value={settings.heroSection}
            onChange={updateHeroSection}
            companyId={companyId}
            defaultHeight={400}
            sectionOptions={[
              { label: 'Referanslar', value: 'references-grid' },
              { label: 'SSS', value: 'references_faq' },
            ]}
          />
        </AccordionDetails>
      </Accordion>

      {/* Display Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceReferencesSettings.sections.viewSettings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('ecommerceReferencesSettings.fields.logosPerRow')}</InputLabel>
                <Select
                  value={settings.displaySettings.itemsPerRow}
                  onChange={(e) => updateDisplaySettings('itemsPerRow', e.target.value)}
                  label={t('ecommerceReferencesSettings.fields.logosPerRow')}
                >
                  <MenuItem value={2}>{t('ecommerceReferencesSettings.options.twoColumns')}</MenuItem>
                  <MenuItem value={3}>{t('ecommerceReferencesSettings.options.threeColumns')}</MenuItem>
                  <MenuItem value={4}>{t('ecommerceReferencesSettings.options.fourColumns')}</MenuItem>
                  <MenuItem value={5}>{t('ecommerceReferencesSettings.options.fiveColumns')}</MenuItem>
                  <MenuItem value={6}>{t('ecommerceReferencesSettings.options.sixColumns')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('ecommerceReferencesSettings.fields.logoSize')}</InputLabel>
                <Select
                  value={settings.displaySettings.logoSize}
                  onChange={(e) => updateDisplaySettings('logoSize', e.target.value)}
                  label={t('ecommerceReferencesSettings.fields.logoSize')}
                >
                  <MenuItem value="small">{t('ecommerceReferencesSettings.options.logoSizeSmall')}</MenuItem>
                  <MenuItem value="medium">{t('ecommerceReferencesSettings.options.logoSizeMedium')}</MenuItem>
                  <MenuItem value="large">{t('ecommerceReferencesSettings.options.logoSizeLarge')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.displaySettings.showCompanyName}
                      onChange={(e) => updateDisplaySettings('showCompanyName', e.target.checked)}
                    />
                  }
                  label={t('ecommerceReferencesSettings.fields.showCompanyName')}
                />
                                 <FormControlLabel
                   control={
                     <Switch
                       checked={settings.displaySettings.showDescription}
                       onChange={(e) => updateDisplaySettings('showDescription', e.target.checked)}
                     />
                   }
                   label={t('ecommerceReferencesSettings.fields.showDescription')}
                 />
                 <FormControlLabel
                   control={
                     <Switch
                       checked={settings.displaySettings.showCategory}
                       onChange={(e) => updateDisplaySettings('showCategory', e.target.checked)}
                     />
                   }
                   label={t('ecommerceReferencesSettings.fields.showCategoryIndustry')}
                 />
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Referanslar */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceReferencesSettings.references.title', { count: settings.references.length })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddReference}
            >
              {t('ecommerceReferencesSettings.references.addNew')}
            </Button>
          </Box>

          <Grid container spacing={2}>
            {settings.references.map((reference) => (
              <Grid item xs={12} md={6} lg={4} key={reference.id}>
                <Card>
                  {reference.logoUrl && (
                    <Box sx={{ height: 120, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, backgroundColor: '#f5f5f5' }}>
                      <img 
                        src={reference.logoUrl} 
                        alt={reference.companyName}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {reference.companyName || t('ecommerceReferencesSettings.references.companyNameFallback')}
                    </Typography>
                    {reference.category && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {reference.category}
                      </Typography>
                    )}
                    {reference.description && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {reference.description.substring(0, 100)}...
                      </Typography>
                    )}
                    {reference.websiteUrl && (
                      <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                        {reference.websiteUrl}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <IconButton
                      size="small"
                      onClick={() => handleEditReference(reference)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteReference(reference.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Referans Düzenleme Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingReference?.id && settings.references.find(ref => ref.id === editingReference.id)
            ? t('ecommerceReferencesSettings.dialog.editCompanyReference')
            : t('ecommerceReferencesSettings.dialog.addCompanyReference')}
        </DialogTitle>
        <DialogContent>
          {editingReference && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceReferencesSettings.dialog.companyNameRequired')}
                  value={editingReference.companyName}
                  onChange={(e) => setEditingReference({ ...editingReference, companyName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceReferencesSettings.dialog.categoryIndustry')}
                  value={editingReference.category || ''}
                  onChange={(e) => setEditingReference({ ...editingReference, category: e.target.value })}
                  placeholder={t('ecommerceReferencesSettings.dialog.categoryPlaceholder')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website URL'si"
                  value={editingReference.websiteUrl || ''}
                  onChange={(e) => setEditingReference({ ...editingReference, websiteUrl: e.target.value })}
                  placeholder="https://www.sirket.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={t('ecommerceAboutSettings.fields.description')}
                  value={editingReference.description || ''}
                  onChange={(e) => setEditingReference({ ...editingReference, description: e.target.value })}
                  placeholder={t('ecommerceReferencesSettings.dialog.shortDescriptionOptional')}
                />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography gutterBottom>{t('ecommerceReferencesSettings.dialog.companyLogo')}</Typography>
                  <Button variant="outlined" component="label" startIcon={<ImageIcon />}>
                    {t('ecommerceReferencesSettings.dialog.selectLogo')}
                    <input type="file" hidden accept="image/*" onChange={handleImageFileChange} />
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    {t('ecommerceReferencesSettings.dialog.logoHelp')}
                  </Typography>
                  {imagePreview && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, display: 'inline-block' }}>
                      <img 
                        src={imagePreview} 
                        alt={t('ecommerceReferencesSettings.dialog.logoPreviewAlt')}
                        style={{ maxWidth: '150px', maxHeight: '80px', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('ecommerceAboutSettings.actions.cancel')}</Button>
          <Button 
            onClick={handleSaveReference} 
            variant="contained"
            disabled={imageUploading || !editingReference?.companyName}
          >
            {imageUploading ? t('ecommerceReferencesSettings.dialog.uploading') : t('ecommerceReferencesSettings.dialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ReferencesSettingsComponent;
